import { normalizeSeverity, severityRank, isFlaggedSeverity } from './severity'

const SCOPE_LEVEL_LABELS = {
    portfolio: 'Portfolio',
    hierarchy_node: 'Hierarchy',
    entity: 'Entity',
}

// Severity handling lives in ./severity (single source of truth — audit F3).
// Re-exported here because most DI components import these via hierarchyUtils.
export { normalizeSeverity, severityRank }

export const getEntityId = (entity) =>
    entity?.entityId || entity?.scopeEntityId || entity?.label || entity?.id || ''

export const getEntityDisplayId = (entity) =>
    entity?.displayEntityId || entity?.displayLabel || getEntityId(entity)

export const getEntityTypeLabel = (report, entity) =>
    report?.request?.entityType || entity?.entityType || 'Entity'

export const getEntityKeyFinding = (entity) => {
    if (entity?.keyFinding) return entity.keyFinding
    if (entity?.diagnosis) return entity.diagnosis
    if (Array.isArray(entity?.keyFindings) && entity.keyFindings.length > 0) {
        return entity.keyFindings[0]
    }
    if (entity?.synthesis) return entity.synthesis
    return ''
}

export const isFlaggedEntity = (entity) => {
    if (!entity) return false
    if (entity.hitScreen === true) return true
    return isFlaggedSeverity(entity.severity)
}

export const getHierarchyEntries = (entity, hierarchyLevels = []) => {
    const hierarchy = entity?.hierarchy
    if (!hierarchy || typeof hierarchy !== 'object') return []

    const configuredLevels = hierarchyLevels.filter((level) => hierarchy[level])
    const levels = configuredLevels.length > 0 ? configuredLevels : Object.keys(hierarchy)

    return levels
        .map((levelName) => ({
            levelName,
            nodeId: hierarchy[levelName],
        }))
        .filter(
            (entry) => entry.nodeId !== undefined && entry.nodeId !== null && entry.nodeId !== ''
        )
}

export const getEntityHierarchyPathLabel = (entity, hierarchyLevels = []) =>
    getHierarchyEntries(entity, hierarchyLevels)
        .map((entry) => entry.nodeId)
        .join(' / ')

const makeHierarchyNode = ({ id, levelName, nodeId, parentId, depth }) => ({
    id,
    label: nodeId,
    nodeType: 'hierarchy_node',
    levelName,
    nodeId,
    parentId,
    depth,
    entityCount: 0,
    flaggedCount: 0,
    severeCount: 0,
    moderateCount: 0,
    children: [],
})

const addCounts = (node, entity) => {
    node.entityCount += 1
    if (isFlaggedEntity(entity)) node.flaggedCount += 1

    const severity = normalizeSeverity(entity.severity)
    if (severityRank(severity) <= severityRank('severe')) {
        node.severeCount += 1
    } else if (severityRank(severity) <= severityRank('moderate')) {
        node.moderateCount += 1
    }
}

const sortHierarchyNodes = (nodes) => {
    nodes.sort((a, b) => {
        const byFlagged = (b.flaggedCount || 0) - (a.flaggedCount || 0)
        if (byFlagged !== 0) return byFlagged
        return String(a.label).localeCompare(String(b.label), undefined, { numeric: true })
    })

    for (const node of nodes) {
        if (node.children?.length) sortHierarchyNodes(node.children)
    }
}

export const buildHierarchyTree = (entities = [], hierarchyLevels = []) => {
    const root = { id: '__root__', children: [] }
    const nodeMap = new Map()

    for (const entity of entities) {
        const entries = getHierarchyEntries(entity, hierarchyLevels)
        if (entries.length === 0) continue

        let parent = root
        let parentId = root.id
        let path = ''

        for (let depth = 0; depth < entries.length; depth += 1) {
            const entry = entries[depth]
            path = `${path}/${entry.levelName}:${entry.nodeId}`
            const id = `hierarchy:${path}`
            let node = nodeMap.get(id)

            if (!node) {
                node = makeHierarchyNode({
                    id,
                    levelName: entry.levelName,
                    nodeId: entry.nodeId,
                    parentId,
                    depth,
                })
                nodeMap.set(id, node)
                parent.children.push(node)
            }

            addCounts(node, entity)
            parent = node
            parentId = id
        }

        parent.children.push({
            ...entity,
            nodeType: entity.nodeType || 'entity',
            label: getEntityDisplayId(entity),
            entityId: getEntityId(entity),
            keyFinding: getEntityKeyFinding(entity),
        })
    }

    sortHierarchyNodes(root.children)
    return root.children
}

export const flattenHierarchyTree = (nodes = []) => {
    const rows = []
    const visit = (node) => {
        rows.push(node)
        for (const child of node.children || []) {
            if (child.nodeType === 'hierarchy_node') visit(child)
        }
    }
    nodes.forEach(visit)
    return rows
}

export const getDescendantEntities = (node) => {
    const entities = []
    const visit = (current) => {
        for (const child of current?.children || []) {
            if (child.nodeType === 'hierarchy_node') visit(child)
            else if (child.nodeType === 'entity') entities.push(child)
        }
    }
    visit(node)
    return entities
}

export const getActionScopeLabel = (candidate) => {
    if (!candidate) return 'Unscoped'
    if (candidate.scopeType === 'portfolio') return 'Portfolio'
    if (candidate.scopeType === 'hierarchy_node') {
        return `${candidate.scopeLevelName || 'Hierarchy'} ${candidate.scopeNodeId || ''}`.trim()
    }
    if (candidate.scopeEntityId) {
        return `${candidate.scopeLevelName || 'Entity'} ${candidate.scopeEntityId}`.trim()
    }
    if (candidate.scopeNodeId) {
        return `${candidate.scopeLevelName || SCOPE_LEVEL_LABELS[candidate.scopeType] || 'Scope'} ${candidate.scopeNodeId}`.trim()
    }
    return SCOPE_LEVEL_LABELS[candidate.scopeType] || candidate.scopeLevelName || 'Unscoped'
}

export const getActionScopeGroup = (candidate) => {
    if (candidate?.scopeType === 'portfolio') return 'Portfolio'
    if (candidate?.scopeType === 'hierarchy_node') {
        return candidate.scopeLevelName || 'Hierarchy Nodes'
    }
    if (candidate?.scopeType === 'entity' || candidate?.scopeEntityId) {
        return candidate.scopeLevelName || 'Entities'
    }
    return 'Unscoped'
}

export const actionMatchesEntity = (candidate, entity) => {
    if (!candidate || !entity) return false
    if (candidate.scopeType === 'portfolio') return true
    if (
        candidate.scopeEntityId &&
        [getEntityId(entity), getEntityDisplayId(entity)].includes(candidate.scopeEntityId)
    ) {
        return true
    }
    if (candidate.scopeType === 'hierarchy_node') {
        return Boolean(
            candidate.scopeLevelName &&
                candidate.scopeNodeId &&
                entity?.hierarchy?.[candidate.scopeLevelName] === candidate.scopeNodeId
        )
    }
    return false
}

export const actionMatchesHierarchyNode = (candidate, hierarchyNode) => {
    if (!candidate || !hierarchyNode) return false
    if (
        candidate.scopeType === 'hierarchy_node' &&
        candidate.scopeLevelName === hierarchyNode.levelName &&
        candidate.scopeNodeId === hierarchyNode.nodeId
    ) {
        return true
    }

    if (candidate.scopeEntityId) {
        return getDescendantEntities(hierarchyNode).some(
            (entity) => getEntityId(entity) === candidate.scopeEntityId
        )
    }

    return false
}
